import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FiX, FiUpload, FiDownload, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import { csvAPI } from '../lib/api';

const CsvExtractModal = ({ document, onClose }) => {
  const [templateFile, setTemplateFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [error, setError] = useState(null);
  const queryClient = useQueryClient();

  const extractMutation = useMutation({
    mutationFn: (formData) => csvAPI.extract(formData),
    onSuccess: (response) => {
      setExtractionResult(response.data);
      setError(null);
      queryClient.invalidateQueries(['csv-extractions', document.id]);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to extract CSV data');
      setExtractionResult(null);
    },
  });

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      const file = files[0];
      if (file.name.endsWith('.csv') || file.type === 'text/csv') {
        setTemplateFile(file);
        setError(null);
      } else {
        setError('Please upload a CSV file');
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.name.endsWith('.csv') || file.type === 'text/csv') {
        setTemplateFile(file);
        setError(null);
      } else {
        setError('Please upload a CSV file');
      }
    }
  };

  const handleExtract = async () => {
    if (!templateFile) {
      setError('Please upload a CSV template file');
      return;
    }

    const formData = new FormData();
    formData.append('template', templateFile);
    formData.append('documentId', document.id);
    formData.append('templateName', templateFile.name);

    extractMutation.mutate(formData);
  };

  const handleDownload = () => {
    if (extractionResult?.downloadUrl) {
      window.open(extractionResult.downloadUrl, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">CSV Extract</h2>
            <p className="text-sm text-gray-600 mt-1">
              Extract structured data from: <span className="font-medium">{document.name}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">How it works:</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>Upload a CSV template with column headers (and optionally a sample row)</li>
              <li>AI will analyze your document and extract matching data</li>
              <li>Download the generated CSV file with all extracted records</li>
            </ol>
          </div>

          {/* File Upload Area */}
          {!extractionResult && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload CSV Template
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragActive
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <FiUpload className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                <div className="space-y-2">
                  {templateFile ? (
                    <div className="flex items-center justify-center space-x-2">
                      <FiCheckCircle className="text-green-500" size={20} />
                      <span className="text-sm font-medium text-gray-900">
                        {templateFile.name}
                      </span>
                      <button
                        onClick={() => setTemplateFile(null)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <FiX size={18} />
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600">
                        Drag and drop your CSV template here, or click to browse
                      </p>
                      <label className="inline-block">
                        <input
                          type="file"
                          className="hidden"
                          accept=".csv,text/csv"
                          onChange={handleFileChange}
                        />
                        <span className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer inline-block text-sm font-medium">
                          Choose File
                        </span>
                      </label>
                    </>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Example: A CSV with headers like "Item Description, Quantity, Unit Price, Total Amount"
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <FiAlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <h4 className="text-sm font-semibold text-red-900">Error</h4>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Extraction Result */}
          {extractionResult && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 space-y-4">
              <div className="flex items-start space-x-3">
                <FiCheckCircle className="text-green-500 flex-shrink-0 mt-0.5" size={24} />
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-green-900">Extraction Complete!</h4>
                  <p className="text-sm text-green-700 mt-1">
                    Successfully extracted {extractionResult.rowCount} record(s) from the document
                  </p>
                  <div className="mt-4 space-y-2">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">File:</span> {extractionResult.fileName}
                    </p>
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Rows:</span> {extractionResult.rowCount}
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={handleDownload}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
              >
                <FiDownload size={20} />
                <span>Download CSV File</span>
              </button>
            </div>
          )}

          {/* Processing Status */}
          {extractMutation.isPending && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center space-x-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <div>
                  <h4 className="font-semibold text-blue-900">Processing...</h4>
                  <p className="text-sm text-blue-700 mt-1">
                    AI is analyzing your document and extracting data. This may take a moment.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 px-6 py-4 bg-gray-50 border-t rounded-b-lg">
          {!extractionResult ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={extractMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleExtract}
                disabled={!templateFile || extractMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {extractMutation.isPending ? 'Extracting...' : 'Extract Data'}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CsvExtractModal;
